<template lang="pug">
//- A CMS admin's requests to US (T-350, docs/features/TENANT_PLATFORM_TICKETS.md):
//- our platform desk as the website it is about sees it. Deliberately small:
//- list, open one, write a message or add files, file a new one. Everything else on
//- these tickets is our desk's business and is not even in the type.
full-height-drawer(
    :visible="visible"
    :title="title"
    size="760px"
    append-to-body
    wrapper-closable
    @update:visible="$emit('update:visible', $event)"
    @open="showList"
    @keydown.native="onKeydown"
)
    //- Back to the list where the eye starts, as well as in the footer.
    template(#title)
        .drawer-title
            el-button.drawer-title__back(
                v-if="view !== 'list'"
                circle
                size="mini"
                icon="el-icon-fa-arrow-left fas"
                :title="$t('All tickets')"
                @click="showList"
            )
            span.drawer-title__text {{ title }}
    .platform-tickets(v-loading="loading")
        template(v-if="view === 'list'")
            p.platform-tickets__intro {{ $t('Your requests to our support team. We answer here and by email.') }}
            //- Whose tickets (T-122): only offered when the website has the
            //- user's own and a colleague's, and then their own come first.
            el-radio-group.platform-tickets__contact(
                v-if="contactName"
                :value="contactFilter"
                size="small"
                @input="filterByContact"
            )
                el-radio-button(label="mine") {{ $t('Tickets of {name}', { name: contactName }) }}
                el-radio-button(label="all") {{ $t('All tickets') }}
            //- Only once there is something to search through or to switch to.
            //- The search covers every ticket, so while it runs neither
            //- segment is chosen; choosing one ends the search.
            .platform-tickets__toolbar(v-if="searchable || closedCount")
                el-input.platform-tickets__search(
                    v-model="search"
                    size="small"
                    clearable
                    prefix-icon="el-icon-search"
                    :placeholder="$t('Search by subject or number')"
                )
                el-radio-group.platform-tickets__status(
                    v-if="closedCount || showClosed"
                    :value="statusFilter"
                    size="small"
                    @input="filterByStatus"
                )
                    el-radio-button(label="open") {{ $t('Open') }}
                    el-radio-button(label="closed")
                        | {{ $t('Closed') }}
                        span.platform-tickets__count {{ closedCount }}
            .platform-tickets__empty(v-if="!tickets.length && !loading && !search && !showClosed && !closedCount")
                i.el-icon-fa-headset.fas
                p {{ $t('No tickets yet. Describe what you need and we will take care of it.') }}
                el-button(round size="small" type="primary" icon="el-icon-fa-plus fas" @click="startCreating") {{ $t('Create new ticket') }}
            p.ticket-section__empty(v-else-if="!tickets.length && !loading && search") {{ $t('No ticket matches this search.') }}
            p.ticket-section__empty(v-else-if="!tickets.length && !loading") {{ $t('No open tickets.') }}
            ul.ticket-rows(v-else-if="tickets.length")
                li(v-for="item in tickets" :key="item.id")
                    button.ticket-row(type="button" :class="{ 'ticket-row--unread': item.has_unread_reply }" @click="open(item.id)")
                        span.ticket-row__text
                            span.ticket-row__subject {{ item.subject }}
                            span.ticket-row__meta
                                template(v-if="item.has_unread_reply")
                                    span.ticket-row__new {{ $t('New reply') }}
                                    | ,&nbsp;
                                | {{ $t('{number}, since {date}', { number: item.number, date: formatDate(item.start_date) }) }}
                                //- "von", as in the "Tickets von {name}" switch; left
                                //- out under that switch, where it is always the user.
                                template(v-if="contactPersonName(item) && !showingOwnTickets") , {{ $t('by {name}', { name: contactPersonName(item) }) }}
                        el-tag.ticket-row__status(size="mini" :type="statusTagType(item.status)") {{ $t('ticket_status.' + item.status) }}
                        i.ticket-row__chevron.el-icon-arrow-right
            el-pagination.platform-tickets__pagination(
                v-if="total > perPage"
                small
                layout="prev, pager, next"
                :total="total"
                :page-size="perPage"
                :current-page="page"
                @current-change="goToPage"
            )

        el-form(
            v-else-if="view === 'create'"
            ref="createForm"
            label-position="top"
            size="small"
            :model="form"
            :rules="rules"
            :validate-on-rule-change="false"
            @submit.native.prevent
        )
            el-form-item(:label="$t('Name')" prop="subject")
                el-input(ref="subject" v-model="form.subject")
            el-form-item(:label="$t('Description')")
                quill-editor(v-model="form.description" :options="quillConfig")
            el-form-item(:label="$t('Files')")
                ticket-files-field(v-model="form.files" :show-hint="false")

        template(v-else-if="ticket")
            .ticket-head
                el-tag(size="small" :type="statusTagType(ticket.status)") {{ $t('ticket_status.' + ticket.status) }}
                span.ticket-head__fact {{ ticket.number }}
                span.ticket-head__fact {{ $t('Start date') }} {{ formatDate(ticket.start_date) }}
            //- Purified on the server before it is stored.
            .ticket-description(v-if="ticket.description" v-html="ticket.description")
            p.ticket-description.ticket-description--empty(v-else) {{ $t('No description.') }}

            section.ticket-section
                h4.ticket-section__title {{ $t('Files') }}
                //- A closed ticket only takes a follow-up: its files are shown, not added to.
                ticket-files-field(
                    v-if="ticket.status !== 'closed'"
                    :files="files"
                    :ticket="ticket"
                    :removable="false"
                    :show-hint="false"
                    @change="attachFiles"
                )
                p.ticket-section__empty(v-else-if="!files.length") {{ $t('No files.') }}
                ul.closed-files(v-else)
                    li(v-for="file in files" :key="file.id")
                        a(:href="file.signed_url" target="_blank" rel="noopener") {{ file.preferred_name || file.name }}

            section.ticket-section
                h4.ticket-section__title {{ $t('History') }}
                p.ticket-section__empty(v-if="!ticket.messages.length") {{ $t('No messages yet. Our answers appear here as well.') }}
                //- The SAME renderers as our own ticket thread: an email is the
                //- inbox's mail card, a chat the inbox's bubble on its wallpaper.
                //- Without the agent's controls, and the customer's own mail is
                //- never "unread" here (that read state is ours).
                .thread(v-else :class="{ 'is-whatsapp': ticket.channel === 'whatsapp' }")
                    template(v-for="message in threadMessages")
                        .thread-divider(v-if="message.id === firstNewReplyId" :key="'new-' + message.id") {{ $t('New reply') }}
                        email-message-card(
                            v-if="message.channel === 'email'"
                            :key="message.id"
                            :message="message"
                            :default-expanded="expandsByDefault(message)"
                            :allow-forward="false"
                            :agent-actions="false"
                        )
                        chat-bubble(
                            v-else
                            :key="message.id"
                            :message="message"
                            :author="message.fromUs ? $t('CityOffer support') : ''"
                        )
                .composer(v-if="ticket.status !== 'closed'")
                    //- The editor its thread speaks: our reply composer's for a mail,
                    //- WhatsApp's (only what WhatsApp can show) for a chat.
                    //- Quill reads its options once: a new channel needs a new editor.
                    quill-editor.composer__editor(
                        :key="'composer-' + (ticket.channel || 'email')"
                        ref="newMessage"
                        v-model="newMessage"
                        :options="composerConfig"
                    )
                    .composer__actions
                        el-button(
                            round
                            size="small"
                            type="primary"
                            icon="el-icon-fa-paper-plane fas"
                            :loading="sendingMessage"
                            @click="sendMessage"
                        ) {{ $t('Send message') }}
                p.ticket-section__empty(v-else) {{ $t('This ticket is closed. Create a new ticket if you need anything else.') }}

    //- Dismissing lives on the left, the drawer's one action on the right,
    //- like every other drawer (ProjectDrawer, MessagePreset Manager).
    template(#footer-left)
        el-button(round size="mini" icon="el-icon-fa-times fas" @click="$emit('update:visible', false)") {{ $t('Close') }}
        el-button(
            v-if="view !== 'list'"
            round
            size="mini"
            icon="el-icon-fa-arrow-left fas"
            @click="showList"
        ) {{ $t('All tickets') }}
    template(#footer)
        //- An empty list carries the button in its own body.
        el-button(
            v-if="view === 'list' && tickets.length"
            round
            size="mini"
            type="primary"
            icon="el-icon-fa-plus fas"
            @click="startCreating"
        ) {{ $t('Create new ticket') }}
        el-button(
            v-if="view === 'create'"
            round
            size="mini"
            type="primary"
            icon="el-icon-fa-save fas"
            :loading="saving"
            @click="create"
        ) {{ $t('Save') }}
</template>

<i18n>
{
    "de": {
        "New ticket": "Neues Ticket",
        "Create new ticket": "Neues Ticket erstellen",
        "No tickets yet. Describe what you need and we will take care of it.": "Noch keine Tickets. Beschreiben Sie, was Sie brauchen, und wir kümmern uns darum.",
        "Name": "Name",
        "Description": "Beschreibung",
        "Files": "Dateien",
        "Start date": "Startdatum",
        "Add something for our team": "Ergänzen Sie etwas für unser Team",
        "Send message": "Nachricht senden",
        "Search by subject or number": "Nach Betreff oder Nummer suchen",
        "No ticket matches this search.": "Kein Ticket passt zu dieser Suche.",
        "No open tickets.": "Keine offenen Tickets.",
        "Open": "Offen",
        "Closed": "Geschlossen",
        "New reply": "Neue Antwort",
        "New": "Neu",
        "Your requests to our support team. We answer here and by email.": "Ihre Anfragen an unser Support-Team. Wir antworten hier und per E-Mail.",
        "{number}, since {date}": "{number}, seit {date}",
        "No description.": "Keine Beschreibung.",
        "No files.": "Keine Dateien.",
        "History": "Verlauf",
        "No messages yet. Our answers appear here as well.": "Noch keine Nachrichten. Unsere Antworten erscheinen ebenfalls hier.",
        "CityOffer support": "CityOffer Support",
        "You": "Sie",
        "This ticket is closed. Create a new ticket if you need anything else.": "Dieses Ticket ist geschlossen. Erstellen Sie ein neues Ticket, wenn Sie noch etwas brauchen.",
        "All tickets": "Alle Tickets",
        "Tickets of {name}": "Tickets von {name}",
        "by {name}": "von {name}",
        "Close": "Schließen",
        "Save": "Speichern",
        "Ticket created": "Ticket erstellt",
        "Message sent": "Nachricht gesendet",
        "Files added": "Dateien hinzugefügt",
        "Write a message first.": "Bitte zuerst eine Nachricht schreiben.",
        "Something went wrong!": "Etwas ist schiefgelaufen!",
        "yyyy-MM-dd": "dd.MM.yyyy"
    },
    "en": {
        "Tickets of {name}": "Tickets of {name}",
        "by {name}": "by {name}",
        "{number}, since {date}": "{number}, since {date}"
    }
}
</i18n>

<script>
import _ from 'lodash';

import quillConfig from '@/quill/ticketConfig';
import emailComposerConfig from '@/quill/composerConfig';
import whatsappComposerConfig from '@/quill/whatsappConfig';
import { timestamp2date } from '@/filters';
import TicketApi from '@/extensions/tickets/store/api/ticket';
import FullHeightDrawer from '@/components/FullHeightDrawer';
import TicketFilesField from '@/extensions/tickets/components/TicketFilesField';
import { ticketStatusTagType } from '@/extensions/tickets/ticketStatus';
import { contactPersonName } from '@/extensions/tickets/contactPerson';
import { isCommandShortcut } from '@/helpers/shortcuts';
import EmailMessageCard from '@/extensions/communication/views/Conversation/Partials/EmailMessageCard';
import ChatBubble from '@/extensions/communication/views/Conversation/Partials/ChatBubble';
import { useTicketStore } from '@/stores';

// An editor is never empty: it leaves <p><br></p> behind.
const hasText = (html) => !!String(html || '').replace(/<(?!img)[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

const blankForm = () => ({ subject: '', description: '', files: [] });

export default {
    name: 'PlatformTicketsDrawer',
    components: {
        'full-height-drawer': FullHeightDrawer,
        TicketFilesField,
        EmailMessageCard,
        ChatBubble
    },
    filters: { timestamp2date },
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        cmsId: {
            type: [Number, String],
            required: true
        }
    },
    data() {
        return {
            quillConfig,
            view: 'list',
            loading: false,
            saving: false,
            sendingMessage: false,
            tickets: [],
            // One page of them; the server pages and searches.
            page: 1,
            perPage: 20,
            total: 0,
            search: '',
            // The finished ones instead of the rest, and how many there are.
            showClosed: false,
            closedCount: 0,
            // "Tickets von {name}": the user's name when the switch is offered.
            contactName: null,
            contactFilter: 'mine',
            ticket: null,
            form: blankForm(),
            newMessage: '',
            // Our replies that were unread when this ticket was opened: marked
            // read on the server at once, still shown as new until the next open.
            newReplyIds: [],
            rules: { subject: 'required' }
        };
    },
    computed: {
        // The messages as the shared renderers read them, from the WEBSITE'S
        // side of the conversation: what they wrote goes out (up arrow, right
        // bubble), our replies come in. Our replies are signed by the support
        // team, never by the person, and nothing is marked unread (that read
        // state is ours). The card names an outgoing mail by its user and an
        // incoming one by its sender.
        threadMessages() {
            return ((this.ticket && this.ticket.messages) || []).map((message) => {
                const fromUs = message.direction === 'outbound';

                return {
                    ...message,
                    direction: fromUs ? 'inbound' : 'outbound',
                    is_read: true,
                    user: fromUs ? null : { first_name: this.$t('You') },
                    from_address: fromUs ? this.$t('CityOffer support') : null,
                    // The card previews an outgoing mail from its quote body, which
                    // the flip above makes of theirs: the body, already cleaned.
                    quote_body: fromUs ? null : message.body,
                    fromUs
                };
            });
        },
        firstNewReplyId() {
            const first = this.threadMessages.find((message) => this.newReplyIds.includes(message.id));

            return first ? first.id : null;
        },
        composerConfig() {
            const config = _.get(this.ticket, 'channel') === 'whatsapp' ? whatsappComposerConfig : emailComposerConfig;
            // Without the template variables' {{ }} tool: a shop writing to us has none.
            const container = config.modules.toolbar.container.filter((group) => !_.isEqual(group, ['placeholder']));

            return {
                ...config,
                modules: { ...config.modules, toolbar: { ...config.modules.toolbar, container } },
                placeholder: this.$t('Add something for our team')
            };
        },
        // No segment while a search runs over every ticket.
        statusFilter() {
            if (this.search) return null;

            return this.showClosed ? 'closed' : 'open';
        },
        // "Tickets von {name}" is offered and chosen.
        showingOwnTickets() {
            return !!this.contactName && this.contactFilter === 'mine';
        },
        searchable() {
            return this.total > this.perPage || !!this.search;
        },
        title() {
            if (this.view === 'create') return this.$t('New ticket');
            if (this.view === 'detail' && this.ticket) return this.ticket.subject;

            return this.$t('Tickets');
        },
        // What the ticket holds, marked as held so a resend never re-converts it.
        files() {
            return ((this.ticket && this.ticket.files) || []).map((file) => ({ ...file, upload: false }));
        }
    },
    created() {
        this.searchSoon = _.debounce(() => {
            this.page = 1;
            this.loadPage();
        }, 300);
    },
    watch: {
        // Another website, another set of requests.
        cmsId() {
            this.page = 1;
            this.search = '';
            this.showClosed = false;
            this.contactName = null;
            this.contactFilter = 'mine';
            if (this.visible) this.showList();
        },
        search() {
            this.searchSoon();
        }
    },
    methods: {
        statusTagType: ticketStatusTagType,
        contactPersonName,
        // start_date arrives as Y-m-d
        formatDate: (value) => (value ? value.split('-').reverse().join('.') : ''),
        notify(message, type = 'success') {
            this.$notify[type]({
                title: message,
                iconClass: type === 'error' ? 'fas el-icon-fa-xmark' : 'fas el-icon-fa-check',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000
            });
        },
        failed(error) {
            /* eslint no-console: ["error", { allow: ["error"] }] */
            console.error(error);
            this.notify(_.get(error, 'graphQLErrors.0.message') || this.$t('Something went wrong!'), 'error');
        },
        load(request) {
            this.loading = true;

            return request.catch(this.failed).finally(() => {
                this.loading = false;
            });
        },
        showList() {
            this.view = 'list';
            this.ticket = null;
            // Asked first: the list it decides comes right after.
            TicketApi.fetchTenantPlatformTicketContactFilter(this.cmsId)
                .then((name) => {
                    this.contactName = name || null;
                })
                .catch(this.failed)
                .finally(() => this.loadPage());
        },
        loadPage() {
            const search = this.search.trim();
            const mine = this.showingOwnTickets;

            TicketApi.fetchTenantPlatformClosedTicketCount(this.cmsId, { mine })
                .then((count) => {
                    this.closedCount = count || 0;
                })
                .catch(this.failed);

            this.load(TicketApi.fetchTenantPlatformTickets(this.cmsId, { page: this.page, search, closed: this.showClosed, mine })).then((result) => {
                // A newer search has been typed since this one left.
                if (!result || search !== this.search.trim()) return;

                this.tickets = result.data;
                this.total = result.total;
                this.perPage = result.per_page;
                this.page = result.current_page;
            });
        },
        filterByStatus(status) {
            this.showClosed = status === 'closed';
            this.page = 1;
            if (this.search) {
                // Clearing the search reloads through its own watcher.
                this.search = '';
                return;
            }
            this.loadPage();
        },
        filterByContact(filter) {
            this.contactFilter = filter;
            this.page = 1;
            this.loadPage();
        },
        goToPage(page) {
            this.page = page;
            this.loadPage();
            // The pager sits under the list: a new page starts at its top.
            const scroller = this.$el.querySelector('.drawer-body');
            if (scroller) scroller.scrollTop = 0;
        },
        open(id) {
            this.newMessage = '';
            this.load(TicketApi.fetchTenantPlatformTicket(this.cmsId, id)).then((ticket) => {
                if (!ticket) return;

                this.ticket = ticket;
                this.view = 'detail';
                // A colleague of the contact person sees nothing new and reads
                // nothing: the server decides for whom the replies are.
                this.newReplyIds = ticket.has_unread_reply
                    ? ticket.messages.filter((message) => message.direction === 'outbound' && !message.read_at).map((message) => message.id)
                    : [];
                if (this.newReplyIds.length) this.markRead(ticket.id);
            });
        },
        // Opening is reading. The row and the header badge catch up here; the
        // count announcement does the same for other open tabs.
        // Like the inbox: the new replies open, and the last message, so the
        // thread reads from where the conversation stands.
        expandsByDefault(message) {
            const last = this.threadMessages[this.threadMessages.length - 1];

            return this.newReplyIds.includes(message.id) || (!!last && last.id === message.id);
        },
        markRead(id) {
            TicketApi.markTenantPlatformTicketRead(this.cmsId, id)
                .then(() => {
                    this.tickets = this.tickets.map((item) => (item.id === id ? { ...item, has_unread_reply: false } : item));
                    useTicketStore().fetchTenantPlatformUnreadCount();
                })
                .catch(this.failed);
        },
        startCreating() {
            this.form = blankForm();
            this.view = 'create';
            this.$nextTick(() => this.$refs.subject && this.$refs.subject.focus());
        },
        create() {
            if (this.saving) return;

            this.$refs.createForm.validate((valid) => {
                if (!valid) return;

                this.saving = true;
                TicketApi.createTenantPlatformTicket(this.cmsId, {
                    subject: this.form.subject.trim(),
                    description: this.form.description || null,
                    files: this.form.files
                })
                    .then((ticket) => {
                        this.notify(this.$t('Ticket created'), 'success');
                        this.ticket = ticket;
                        this.view = 'detail';
                    })
                    .catch(this.failed)
                    .finally(() => {
                        this.saving = false;
                    });
            });
        },
        sendMessage() {
            if (this.sendingMessage) return;

            const body = hasText(this.newMessage) ? this.newMessage : '';
            if (!body) {
                this.notify(this.$t('Write a message first.'), 'warning');
                return;
            }

            this.sendingMessage = true;
            TicketApi.createTenantPlatformTicketReply(this.cmsId, this.ticket.id, body)
                .then((ticket) => {
                    this.ticket = ticket;
                    this.newMessage = '';
                    this.notify(this.$t('Message sent'), 'success');
                })
                .catch(this.failed)
                .finally(() => {
                    this.sendingMessage = false;
                });
        },
        // The field reports the whole set; only what is new goes to the server,
        // which adds it to what the ticket already holds.
        attachFiles(files) {
            const held = this.files.map((file) => file.id);
            const added = files.filter((file) => !held.includes(file.id));
            if (!added.length) return;

            this.load(TicketApi.attachTenantPlatformTicketFiles(this.cmsId, this.ticket.id, added)).then((ticket) => {
                if (!ticket) return;

                this.ticket = ticket;
                this.notify(this.$t('Files added'), 'success');
            });
        },
        // ⌘S / Ctrl+S saves what this view saves, wherever the focus is in the
        // drawer (footer included); Esc is the drawer's own.
        onKeydown(event) {
            if (!isCommandShortcut(event, 's')) return;

            event.preventDefault();
            if (this.view === 'create') this.create();
            else if (this.view === 'detail' && hasText(this.newMessage)) this.sendMessage();
        }
    }
};
</script>

<style lang="sass" scoped>
.drawer-title
    display: flex
    align-items: center
    gap: 10px
    min-width: 0

    &__back
        flex-shrink: 0

    &__text
        overflow: hidden
        text-overflow: ellipsis
        white-space: nowrap

.platform-tickets__intro
    margin: 0 0 16px
    font-size: .86rem
    line-height: 1.5
    color: #606266

// An empty desk is an invitation to write, not a blank drawer.
.platform-tickets__empty
    display: flex
    flex-direction: column
    align-items: center
    gap: 12px
    padding: 56px 24px
    border: 1px dashed #dcdfe6
    border-radius: 8px
    text-align: center
    color: #909399

    i
        font-size: 28px
        color: #c0c4cc

    p
        max-width: 320px
        margin: 0
        font-size: .86rem
        line-height: 1.5

// One request per row: what it is about reads first, its number and age
// second, the status where the eye ends.
// Its own row above search and status: whose tickets first, then which.
.platform-tickets__contact
    display: flex
    margin-bottom: 12px

.platform-tickets__toolbar
    display: flex
    align-items: center
    gap: 10px
    margin-bottom: 12px

.platform-tickets__search
    flex: 1
    min-width: 0

.platform-tickets__status
    // A flex box, not inline-block: its line box sat the segments 2px above the search.
    display: flex
    flex-shrink: 0

// The closed count rides inside its segment as a small pill, not in brackets.
// Its 16px sit in the segment's 12px line: the negative margins keep it from
// making that segment taller than its neighbour and the search field.
.platform-tickets__count
    display: inline-block
    min-width: 16px
    margin: -2px 0 -2px 6px
    vertical-align: top
    padding: 0 4px
    border-radius: 8px
    background: #f0f2f5
    color: #909399
    font-size: 10px
    font-weight: 600
    line-height: 16px
    text-align: center

    .is-active &
        background: rgba(255, 255, 255, .25)
        color: #fff

.platform-tickets__pagination
    margin-top: 12px
    padding: 0
    text-align: center

.ticket-rows
    margin: 0
    padding: 0
    list-style: none
    border: 1px solid #ebeef5
    border-radius: 8px
    overflow: hidden

    li + li
        border-top: 1px solid #ebeef5

.ticket-row
    display: flex
    align-items: center
    gap: 12px
    width: 100%
    padding: 12px 14px
    border: 0
    background: #fff
    text-align: left
    font: inherit
    cursor: pointer

    &:hover,
    &:focus-visible
        background: #f5f7fa
        outline: none

    &:focus-visible
        box-shadow: inset 3px 0 0 #409EFF

    &__text
        display: flex
        flex-direction: column
        gap: 3px
        min-width: 0
        flex: 1

    &__subject
        overflow: hidden
        text-overflow: ellipsis
        white-space: nowrap
        font-size: .9rem
        color: #303133

    &__meta
        font-size: .76rem
        color: #909399

    &__chevron
        color: #c0c4cc

    &__new
        font-weight: 600
        color: #409EFF

    // A reply of ours waits: the subject reads bold, the row carries the
    // brand's left edge.
    &--unread
        box-shadow: inset 3px 0 0 #409EFF

        .ticket-row__subject
            font-weight: 600

.ticket-head
    display: flex
    align-items: center
    flex-wrap: wrap
    gap: 14px
    margin-bottom: 14px

    &__fact
        font-size: .8rem
        color: #909399

.ticket-description
    margin: 0 0 4px
    font-size: .9rem
    line-height: 1.6
    color: #303133
    word-break: break-word

    &--empty
        color: #909399

    ::v-deep p
        margin: 0 0 .6em

.ticket-section
    margin-top: 22px
    padding-top: 18px
    border-top: 1px solid #ebeef5

    &__title
        margin: 0 0 12px
        font-size: .86rem
        font-weight: 600
        color: #303133

    &__empty
        margin: 0
        font-size: .82rem
        color: #909399

.closed-files
    margin: 0
    padding-left: 18px
    font-size: .82rem

// Mail cards sit straight in the section, they carry their own frame (a grey
// surface behind them was a box in a box). A chat gets WhatsApp's wallpaper
// behind it, as in the member portal.
.thread
    margin-bottom: 16px

    &.is-whatsapp
        padding: 14px 20px
        border-radius: 4px
        background: #efeae2

.thread-divider
    display: flex
    align-items: center
    gap: 10px
    margin: 4px 0 10px
    font-size: .76rem
    font-weight: 600
    color: #409EFF

    &::before,
    &::after
        content: ""
        flex: 1
        border-top: 1px solid #b3d8ff

.composer
    margin-top: 12px

    &__editor ::v-deep .ql-editor
        min-height: 90px

.composer__actions
    display: flex
    justify-content: flex-end
    margin-top: 8px
</style>
